import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { onAuthChange, initAuth, auth } from '../lib/authService';
import { getEmployeeByEmailSafe, resolveEmployeeForUser, healEmployeeNickname } from '../api/employees';
import { ensureUserRoleClaim } from '../api/auth';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [employeeDetails, setEmployeeDetails] = useState(null);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [loading, setLoading] = useState(true);
  const pollRef = useRef(null);

  const ADMIN_EMAILS = (process.env.REACT_APP_ADMIN_EMAILS || '')
    .toLowerCase().split(',').map((s) => s.trim()).filter(Boolean);

  function normalizeRole(r) {
    const s = String(r || '').toLowerCase().replace(/[\s_-]+/g, '');
    if (['admin', 'administrator', 'superadmin', 'owner'].includes(s)) return 'admin';
    if (['hr', 'hrd', 'humanresources', 'personalia', 'sumberdayamanusia'].includes(s)) return 'hr';
    if (['karyawan', 'employee', 'staff'].includes(s)) return 'karyawan';
    return 'user';
  }

  async function loadSession(session) {
    if (!session?.user) {
      setCurrentUser(null);
      setUserRole(null);
      setEmployeeDetails(null);
      setMustChangePassword(false);
      return;
    }

    const user = auth.currentUser;
    const email = (user?.email || '').toLowerCase();
    let role = normalizeRole(session.user.role);
    let employeeDetails = session.employee || await resolveEmployeeForUser(user.uid, email);

    if (!employeeDetails && email) {
      employeeDetails = await getEmployeeByEmailSafe?.(email);
    }

    if (employeeDetails) {
      healEmployeeNickname(employeeDetails).catch(() => {});
    }
    if (employeeDetails && role === 'user') role = normalizeRole(employeeDetails.role);

    if (role === 'user' && ADMIN_EMAILS.includes(email)) {
      try {
        const res = await ensureUserRoleClaim();
        if (res?.updated) role = 'admin';
        else role = 'admin';
      } catch (e) {
        console.warn('ensureUserRoleClaim failed:', e);
        role = 'admin';
      }
    }

    const sessionCompanyId = session.user?.companyId || employeeDetails?.companyId || employeeDetails?.company_id || 'default';
    const hydratedEmployeeDetails = employeeDetails
      ? { ...employeeDetails, companyId: employeeDetails.companyId || employeeDetails.company_id || sessionCompanyId }
      : null;

    setCurrentUser(user);
    setUserRole(role);
    setEmployeeDetails(hydratedEmployeeDetails);
    setMustChangePassword(!!(session.mustChangePassword || employeeDetails?.firstLoginPasswordChangeRequired));
  }

  useEffect(() => {
    let active = true;

    const unsub = onAuthChange(async (session) => {
      if (!active) return;
      try {
        await loadSession(session);
      } catch (e) {
        console.error('Auth init failed:', e);
        setCurrentUser(null);
        setUserRole('user');
        setEmployeeDetails(null);
        setMustChangePassword(false);
      } finally {
        if (active) setLoading(false);
      }
    });

    initAuth();

    const failsafe = window.setTimeout(() => {
      if (active) setLoading(false);
    }, 4000);

    return () => {
      active = false;
      window.clearTimeout(failsafe);
      unsub();
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  // Poll ringan — jangan menimpa state lokal yang lebih baru tanpa perlu
  useEffect(() => {
    if (!currentUser?.uid) return;
    pollRef.current = setInterval(async () => {
      const email = (currentUser.email || '').toLowerCase();
      const emp = await resolveEmployeeForUser(currentUser.uid, email);
      if (emp) {
        setEmployeeDetails((prev) => {
          if (!prev) return emp;
          // Pertahankan field lokal jika poll belum membawa nilai (race setelah save)
          return {
            ...emp,
            jenisKelamin: emp.jenisKelamin ?? prev.jenisKelamin ?? null,
            birthPlace: emp.birthPlace ?? prev.birthPlace ?? null,
            dateOfBirth: emp.dateOfBirth ?? emp.birthDate ?? prev.dateOfBirth ?? prev.birthDate ?? null,
            birthDate: emp.birthDate ?? emp.dateOfBirth ?? prev.birthDate ?? prev.dateOfBirth ?? null,
            nik: emp.nik ?? prev.nik ?? null,
            address: emp.address ?? prev.address ?? null,
            familyContact: emp.familyContact ?? prev.familyContact ?? null,
            emergencyPhone: emp.emergencyPhone ?? prev.emergencyPhone ?? null,
            workScheduleConfirmed: emp.workScheduleConfirmed ?? prev.workScheduleConfirmed,
            workScheduleId: emp.workScheduleId ?? prev.workScheduleId,
          };
        });
        setMustChangePassword(!!emp.firstLoginPasswordChangeRequired);
      }
    }, 10000);
    return () => clearInterval(pollRef.current);
  }, [currentUser?.uid]);

  const value = {
    currentUser,
    userRole,
    employeeDetails,
    setEmployeeDetails,
    refreshEmployeeDetails: async () => {
      if (!currentUser?.uid) return null;
      const email = (currentUser.email || '').toLowerCase();
      const emp = await resolveEmployeeForUser(currentUser.uid, email);
      if (emp) {
        healEmployeeNickname(emp);
        setEmployeeDetails(emp);
        setMustChangePassword(!!emp.firstLoginPasswordChangeRequired);
      }
      return emp;
    },
    mustChangePassword,
    setMustChangePassword,
    loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
